import { useQuery } from '@apollo/client';
import { plural, t, Trans } from '@lingui/macro';
import ReactHtmlParser from 'html-react-parser';
import React, { useEffect, FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { Redirect, useLocation, useParams } from 'react-router-dom';

import AdminMessageController from 'Components/AdminMessage/AdminMessageController';
import CallToActionSection from 'Components/CallToActionSection/CallToActionSection';
import CategoriesSlider from 'Components/CategoriesSlider/CategoriesSlider';
import CityList from 'Components/CityList/CityList';
import { CurrentUserConsumer, TCurrentUserState } from 'Components/CurrentUser/CurrentUser';
import LatestRequests from 'Components/LatestRequests/LatestRequests';
import NotFound from 'Components/NotFound/NotFound';
import ProvidersSlider from 'Components/ProvidersSlider/ProvidersSlider';
import ReviewsSchema from 'Components/ReviewsSchema/ReviewsSchema';
import SeoLinking from 'Components/SeoLinking/SeoLinking';
import PageView from 'common/PageView/PageView';
import 'common/styles/SlickSlider.scss';
import config from 'config/config';
import seoLinking from 'config/seoLinking';
import { Heading3 } from 'uikit/Heading';
import analytics from 'utils/analytics/analytics';
import { useL4BySlugQuery } from 'utils/graphql/categories';
import { useCityBySlugQuery, useNearbyCitiesQuery } from 'utils/graphql/geolocation';
import routes from 'utils/routeTranslator';

import BreadcrumbSchema from './BreadcrumbSchema';
import {
  l4Redirects,
  l4Redirect,
  neighbourhoodServices,
  getCategoryPageTitle,
  l2Query,
  getInterlinkingRelatedServices,
  RelatedByL2Response,
  RelatedByL2Vars,
  buildCompanyListingHref,
  useRelatedCompanies,
  buildRegisterPageLinking,
} from './L4Category.helpers';
import './L4Category.scss';
import L4CategoryTopSection from './L4CategoryTopSection';
import L4PopularCategories from './L4PopularCategories/L4PopularCategories';

const HASH_TO_OPEN_WIZARD = '#request';

type L4CityRouteParams = {
  slug: keyof typeof l4Redirects;
  city: string;
  neighbourhood?: string;
};

const L4CategoryCity: FC = () => {
  const { pathname, hash } = useLocation();
  const routeParams = useParams<L4CityRouteParams>();
  const l4RedirectSlug = l4Redirects[routeParams.slug];
  const neighbourhoodSlug = routeParams.neighbourhood;

  const { data } = useL4BySlugQuery({
    variables: {
      slug: routeParams.slug,
      city: routeParams.city,
    },
    skip: !!l4RedirectSlug,
  });

  const { data: cityBySlug } = useCityBySlugQuery({
    variables: {
      slug: routeParams.city,
      anonymous: true,
    },
    skip: !!l4RedirectSlug,
  });

  const { data: nearbyCities } = useNearbyCitiesQuery({
    variables: {
      slug: routeParams.city,
      anonymous: true,
    },
    skip: !!l4RedirectSlug,
  });

  const relatedCompanies = useRelatedCompanies(data?.l4BySlug.companyListingSlug, routeParams.city);

  /**
   * SEO:
   * GS-8269 Change interlinking in the "Powiązane usługi" block
   * see https://naspersclassifieds.atlassian.net/browse/GS-8269
   * Extend the interlinking of "zlecenia pages" on L4
   * see https://naspersclassifieds.atlassian.net/browse/GS-8621
   */
  const l2Slug = data?.l4BySlug.l2.slug;
  const { data: l2Data } = useQuery<RelatedByL2Response, RelatedByL2Vars>(l2Query, {
    skip: !l2Slug || !routeParams.city,
    variables: {
      slug: l2Slug,
      city: routeParams.city,
    },
  });

  const openWizard = () => {
    const submitBtn: HTMLElement | null = document.getElementById('locationSelect__submitBtn');

    if (submitBtn) {
      submitBtn.click();
    }
  };

  useEffect(() => {
    if (hash === HASH_TO_OPEN_WIZARD) {
      // Wait until wizard loads
      // TODO: find robust solution
      setTimeout(openWizard, 1000);
    }
  }, [hash]);

  if (l4RedirectSlug) {
    return l4Redirect(l4RedirectSlug, routeParams.city);
  }

  if (!cityBySlug || !data || !l2Data) {
    return null;
  }

  const relatedServices = getInterlinkingRelatedServices(l2Data.l2BySlug?.l4Groups);
  const registrationPages = buildRegisterPageLinking(l2Data.l2BySlug.categories, routeParams.city);

  const { l4BySlug } = data;

  const {
    id,
    name,
    slug,
    l2,
    l3,
    providerCount,
    questionsAndAnswers,
    popularServices,
    requests,
    feedbacks,
    feedbacksInfo,
    providers,
    mobileCoverUrl,
    avgResponseTime,
    companyListingSlug,
  } = l4BySlug;

  const city = cityBySlug.cityBySlug;
  const isNeighbourhood = Boolean(neighbourhoodSlug);
  const neighbourhood = city && city.neighbourhoods?.find((n) => n.slug === neighbourhoodSlug);

  const companyListingHref = companyListingSlug && buildCompanyListingHref(companyListingSlug, city.slug);

  if (isNeighbourhood && !neighbourhood) {
    return <NotFound />;
  }

  const nameLocativeCase: string = city.nameLocativeCase;
  const cityName: string = city.name;
  const regionName: string = city.region.name;
  const cityId: string = city.id;
  const seoText: string | null = questionsAndAnswers;

  const cityNeighbourhood = isNeighbourhood ? neighbourhood?.name : nameLocativeCase || cityName;
  const popularCategoriesTitle = `${t`Popularne usługi`} ${cityNeighbourhood}`;

  const providersCount: number = providerCount < 10 ? 10 : providerCount;
  const feedbacksCount: number = feedbacksInfo.count < 10 ? 10 : feedbacksInfo.count;
  const feedbacksAvgValue: number = feedbacksInfo.averageValue === 0 ? 4.7 : feedbacksInfo.averageValue;

  const pageTitle: string = getCategoryPageTitle(name, cityName, providersCount);

  // eslint-disable-next-line max-len
  const siteSeoDescription: string = t`${name}: ${providersCount} wykonawców ${cityNeighbourhood} - sprawnie, wygodnie, lokalnie! Dodaj zapytanie i szybko nawiąż kontakt. ${feedbacksCount} opinii klientów!`;

  return (
    <CurrentUserConsumer>
      {(context: TCurrentUserState | undefined) => {
        if (context?.currentUser?.isProvider) {
          // Redirect SP
          return <Redirect to={routes.get('front::dashboard')} />;
        }

        const categorySeoLinking: { title: string; url: string }[] = seoLinking && seoLinking[pathname];

        const canonicalPath = isNeighbourhood
          ? routes.get('front::category_city', {
              slug,
              city: city.slug,
              neighbourhood: neighbourhoodSlug,
            })
          : routes.get('front::category', {
              slug,
              city: city.slug,
            });

        const isNeighbourhoodsVisible =
          city?.neighbourhoods && city.neighbourhoods.length > 0 && neighbourhoodServices.find((s) => s.slug === slug);

        return (
          <div className="l4Category">
            <Helmet>
              <title>{pageTitle}</title>
              <meta property="og:title" content={pageTitle} />
              <meta property="og:description" content={siteSeoDescription} />
              <meta property="og:image" content={mobileCoverUrl} />
              <meta name="msapplication-tooltip" content={siteSeoDescription} />
              <meta name="description" content={siteSeoDescription} />
              <meta name="thumbnail" content={mobileCoverUrl} />
              <link rel="canonical" href={config.SITE_URL + canonicalPath} />
              <body className="mobileNotFixedHeader" />
            </Helmet>
            <PageView
              key={`${slug}.${city.slug}`}
              pageLabel="view_list_professional_profile_city"
              city_name={cityName}
              city={parseInt(cityId, 10)}
              l2_id={parseInt(l2.id, 10)}
              l3_id={parseInt(l3.id, 10)}
              l4_id={parseInt(id, 10)}
              l2_slug={l2.slug}
              l3_slug={l3.slug}
              l4_slug={slug}
            />

            <L4CategoryTopSection l4BySlug={l4BySlug} cityBySlug={city} hash={hash} neighbourhood={neighbourhood} />

            {companyListingHref && (
              <CallToActionSection
                tagline={name}
                title={<Trans>Wybierz jednego z najlepiej ocenianych Wykonawców</Trans>}
                action={<Trans>Przejdź do listy</Trans>}
                href={companyListingHref}
                onClick={() => analytics.trackEvent('firma_l4_browse', { l4_slug: slug })}
              />
            )}

            <div className="l4Category__sectionBottom">
              {isNeighbourhoodsVisible && (
                <div className="l4Category__nearbyCities">
                  <div className="l4Category__subtitle">
                    <Heading3 as="h2">
                      <Trans>{name} w dzielnicach w pobliżu</Trans>
                    </Heading3>
                  </div>
                  <CityList
                    categorySlug={slug}
                    categoryName={name}
                    multiColumn
                    /* @ts-expect-error type mismatch */
                    cities={city.neighbourhoods.filter((n) => n.slug !== neighbourhoodSlug)}
                    citySlug={city.slug}
                    isNeighbourhoods
                  />
                </div>
              )}
              {/* @ts-expect-error type mismatch */}
              {!isNeighbourhood && nearbyCities?.nearbyCities?.length > 0 && (
                <div className="l4Category__nearbyCities">
                  <div className="l4Category__subtitle">
                    <Heading3 as="h2">
                      <Trans>{name} w miastach z pobliżu</Trans>
                    </Heading3>
                  </div>
                  {/* @ts-expect-error type mismatch */}
                  <CityList categorySlug={slug} categoryName={name} multiColumn cities={nearbyCities?.nearbyCities} />
                </div>
              )}

              {!isNeighbourhood && (
                <div className="l4Category__relatedCategories">
                  <div className="l4Category__subtitle l4Category__subtitle_related">
                    <Heading3 as="h2">
                      {name} {cityName}: <Trans>Powiązane usługi</Trans>
                    </Heading3>
                  </div>
                  <CategoriesSlider categories={relatedServices} city={city.slug} />
                </div>
              )}

              {relatedCompanies && (
                <div className="l4Category__seoBlock">
                  <div className="l4Category__seo">
                    <div className="l4Category__subtitle">
                      <Heading3 as="h2">
                        <Trans>Firmy w powiązanych usługach</Trans>
                      </Heading3>
                    </div>
                    <SeoLinking links={relatedCompanies} />
                  </div>
                </div>
              )}

              {registrationPages && (
                <div className="l4Category__seoBlock">
                  <div className="l4Category__seo">
                    <div className="l4Category__subtitle">
                      <Heading3 as="h2">
                        <Trans>Powiązane zlecenia</Trans>
                      </Heading3>
                    </div>
                    <SeoLinking links={registrationPages} />
                  </div>
                </div>
              )}

              {Array.isArray(requests) && requests.length > 0 && (
                <div className="l4Category__latestRequests">
                  <div className="l4Category__subtitle l4Category__subtitle_requests">
                    <Heading3 as="h2">
                      <Trans>
                        {name}: ostatnie zapytania {cityNeighbourhood}
                      </Trans>
                    </Heading3>
                  </div>
                  <LatestRequests requests={requests} />
                  <div className="l4Category__addRequest">
                    <button className="l4Category__addRequestButton" type="button" onClick={openWizard}>
                      <Trans>Dodaj zapytanie</Trans>
                    </button>
                  </div>
                </div>
              )}

              {!seoText && (
                <div className="l4Category__popularCategories">
                  <div className="l4Category__subtitle">
                    <Heading3 as="h2">{popularCategoriesTitle}</Heading3>
                  </div>
                  <L4PopularCategories
                    categories={isNeighbourhood ? neighbourhoodServices : popularServices}
                    city={city.slug}
                    /* @ts-expect-error type mismatch */
                    neighbourhoodSlug={isNeighbourhood ? neighbourhood.slug : undefined}
                    isNeighbourhoods={isNeighbourhood}
                  />
                </div>
              )}

              {seoText && (
                <div className="l4Category__seoBlock">
                  <div className="l4Category__seoSidebar">
                    <div className="l4Category__subtitle">
                      <Heading3 as="h2">{popularCategoriesTitle}</Heading3>
                    </div>
                    <L4PopularCategories
                      categories={isNeighbourhood ? neighbourhoodServices : popularServices}
                      city={city.slug}
                      /* @ts-expect-error type mismatch */
                      neighbourhoodSlug={isNeighbourhood ? neighbourhood.slug : undefined}
                      isNeighbourhoods={isNeighbourhood}
                    />
                  </div>
                  {!isNeighbourhood && (
                    <div className="l4Category__seo">
                      <div className="l4Category__seoText">
                        {ReactHtmlParser(
                          seoText
                            .replace('%[Message_time]', avgResponseTime)
                            .replace(
                              '%[Rewiews_number]',
                              plural(feedbacksCount, {
                                one: '# opinia',
                                few: '# opinie',
                                other: '# opinii',
                              })
                            )
                            .replace('%[Rewiews_average]', String(feedbacksAvgValue))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {categorySeoLinking && (
                <div className="l4Category__seoLinking">
                  <div className="l4Category__subtitle">
                    <Heading3 as="h2">
                      <Trans>Najpopularniejsze wyszukiwania</Trans>
                    </Heading3>
                  </div>
                  <SeoLinking links={categorySeoLinking} />
                </div>
              )}

              {Array.isArray(providers) && providers.length > 0 && (
                <div className="l4Category__providers">
                  <div className="l4Category__subtitle">
                    <Heading3 as="h2">
                      <Trans>
                        Wykonawcy usługi {name} {cityNeighbourhood}
                      </Trans>
                    </Heading3>
                  </div>
                  <ProvidersSlider
                    providers={providers}
                    cityName={cityName}
                    cityLat={city.lat}
                    cityLong={city.long}
                    regionName={regionName}
                  />
                </div>
              )}
            </div>
            {context?.currentUser && <AdminMessageController />}
            <BreadcrumbSchema l4Category={l4BySlug} city={cityBySlug.cityBySlug} />
            <ReviewsSchema
              slug={slug}
              city={city.slug}
              imgUrl={mobileCoverUrl}
              title={`${name} ${nameLocativeCase}`}
              description={siteSeoDescription}
              cityName={cityName}
              regionName={regionName}
              lat={city.lat}
              long={city.long}
              feedbacks={feedbacks}
              feedbacksCount={feedbacksInfo.count}
              rating={feedbacksInfo.averageValue}
              categoryName={name}
              lowestPrice={l4BySlug.lowerQuartilePrice}
            />
          </div>
        );
      }}
    </CurrentUserConsumer>
  );
};

export default L4CategoryCity;
